import { Pit } from "./Pit";
import { TESTIDS } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { RotateCcw, RotateCw } from "lucide-react";

/**
 * Renders a mancala board with any rows × cols layout (2×6, 2×10, 4×7, 4×8…).
 *
 * For 4-row games (Tsoro family): row 0 = B outer, 1 = B inner,
 *   2 = A inner, 3 = A outer.
 * For 2-row games (Oware/Mbothe/Ayoayo family): row 0 = B (top),
 *   row 1 = A (bottom).
 *
 * The grid template is sized from board dimensions to avoid Tailwind
 * dynamic-class purging issues.
 */
function coordLabel(row, col, rows) {
  if (rows === 2) {
    const rowLetter = row === 0 ? "B" : "A";
    return `${rowLetter}${col + 1}`;
  }
  const rowLetter = row === 0 ? "B" : row === 1 ? "b" : row === 2 ? "a" : "A";
  return `${rowLetter}${col + 1}`;
}

export function Board({
  board,
  currentPlayer,
  legalSet,
  selected,
  landing,
  relayPit,
  captureSet,
  onPitSelect,
  showHints = true,
  coords = false,
  disabled = false,
  highContrast = false,
  sowingDirection = "CCW",
  showDirectionBanner = true,
}) {
  const rows = board.length;
  const cols = board[0]?.length || 0;
  const key = (r, c) => `${r},${c}`;
  const isLegal = (r, c) => legalSet && legalSet.has(key(r, c));
  const isSelected = (r, c) => selected && selected[0] === r && selected[1] === c;
  const isLanding = (r, c) => landing && landing[0] === r && landing[1] === c;
  const isRelay = (r, c) => relayPit && relayPit[0] === r && relayPit[1] === c;
  const isCapture = (r, c) => captureSet && captureSet.has(key(r, c));
  // Ownership: 2-row games split at center; 4-row games split at center too.
  const ownerOf = (r) => {
    if (rows === 2) return r === 0 ? "B" : "A";
    return r <= 1 ? "B" : "A";
  };

  return (
    <div
      data-testid={TESTIDS.gameBoard}
      role="grid"
      aria-label={`Mancala game board, ${rows} rows of ${cols} pits${showDirectionBanner ? `, ${sowingDirection === "CW" ? "clockwise" : "anti-clockwise"} sowing` : ""}`}
      className={cn(
        "board-surface relative rounded-3xl",
        "p-3 sm:p-5 md:p-6",
        "shadow-[0_30px_60px_-20px_rgba(0,0,0,0.45),0_8px_24px_-12px_rgba(0,0,0,0.4)]"
      )}
    >
      {showDirectionBanner && (
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="flex items-center gap-2">
            <PlayerBadge player="B" current={currentPlayer === "B"} />
          </div>
          <span
            data-testid="direction-indicator"
            className="text-[10px] sm:text-xs uppercase tracking-widest font-semibold text-foreground/80 bg-background/70 backdrop-blur px-2 py-1 rounded-full"
          >
            {sowingDirection === "CW" ? (
              <span className="inline-flex items-center gap-1.5">
                <RotateCw className="h-3 w-3" /> Clockwise sowing
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <RotateCcw className="h-3 w-3" /> Anti-clockwise sowing
              </span>
            )}
          </span>
          <div className="flex items-center gap-2 invisible sm:visible">
            <PlayerBadge player="A" current={currentPlayer === "A"} />
          </div>
        </div>
      )}

      <div
        className="grid gap-1.5 sm:gap-2.5 md:gap-3"
        style={{
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        }}
      >
        {board.map((rowArr, r) =>
          rowArr.map((count, c) => (
            <Pit
              key={`${r}-${c}`}
              row={r}
              col={c}
              count={count}
              ownerHint={ownerOf(r)}
              isLegal={isLegal(r, c)}
              isSelected={isSelected(r, c)}
              isLanding={isLanding(r, c)}
              isRelay={isRelay(r, c)}
              isCaptureSource={isCapture(r, c)}
              onSelect={onPitSelect}
              showHints={showHints}
              coordLabel={coords ? coordLabel(r, c, rows) : undefined}
              disabled={disabled}
              highContrast={highContrast}
            />
          ))
        )}
      </div>

      {/* Row ownership divider, low-contrast */}
      <div className="absolute left-3 right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="h-px bg-foreground/20" />
      </div>
    </div>
  );
}

function PlayerBadge({ player, current }) {
  return (
    <span
      className={cn(
        "text-[10px] sm:text-xs uppercase tracking-widest font-semibold px-2 py-1 rounded-full",
        current
          ? "bg-primary text-primary-foreground"
          : "bg-background/60 text-foreground/70 backdrop-blur"
      )}
    >
      {player === "A" ? "Player 1" : "Player 2"}
    </span>
  );
}
